import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Alert,
  Paper,
  Snackbar,
  Divider,
  Chip,
  Collapse,
} from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';
import { subDays } from 'date-fns';

import { generateWeeklyTLDR, editWeeklyTLDR } from '../api/dashboard';
import { copyTextToClipboard, copyHtmlToClipboard } from '../utils/clipboard';
import type { FilterOption, WeeklyTLDRResponse } from '../types';
import { formatOrgDisplayName } from '../utils/display';

// Suggestion prompts for editing the TLDR
const EDIT_SUGGESTIONS = [
  'Add more detail on user X',
  'More specific action items',
  'Shorten the report',
  'Focus on concerns',
  'Add recommendations',
];

interface WeeklyTLDRModalProps {
  open: boolean;
  onClose: () => void;
  availableOrgs: FilterOption[];
  preselectedOrgId?: string;
}

export default function WeeklyTLDRModal({
  open,
  onClose,
  availableOrgs,
  preselectedOrgId,
}: WeeklyTLDRModalProps) {
  const preselectedOrg = preselectedOrgId
    ? availableOrgs.find((o) => o.id === preselectedOrgId) || null
    : null;

  const [selectedOrg, setSelectedOrg] = useState<FilterOption | null>(preselectedOrg);
  const [isGenerating, setIsGenerating] = useState(false);
  const [tldrData, setTldrData] = useState<WeeklyTLDRResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [copied, setCopied] = useState(false);
  const [editInstructions, setEditInstructions] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showActiveUsers, setShowActiveUsers] = useState(false);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  const editSectionRef = useRef<HTMLDivElement>(null);

  // Scroll to edit section when TLDR is generated
  useEffect(() => {
    if (tldrData && editSectionRef.current) {
      setTimeout(() => {
        editSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [tldrData]);

  const handleGenerate = useCallback(async () => {
    if (!selectedOrg) return;

    setIsGenerating(true);
    setError(null);
    setTldrData(null);

    try {
      // Default to last 7 days
      const endDate = new Date();
      const startDate = subDays(endDate, 7);

      const data = await generateWeeklyTLDR(selectedOrg.id, startDate, endDate);
      setTldrData(data);
    } catch (err) {
      console.error('Failed to generate TLDR:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedOrg]);

  const handleCopy = useCallback(async () => {
    if (!tldrData) return;

    const success = await copyTextToClipboard(tldrData.markdownContent);
    if (success) {
      setCopied(true);
      setSnackbar({ open: true, message: 'Report copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    } else {
      setSnackbar({ open: true, message: 'Failed to copy.' });
    }
  }, [tldrData]);

  const handleEmail = useCallback(async () => {
    if (!tldrData) return;

    // Convert markdown to HTML for rich text email
    const htmlContent = await marked(tldrData.markdownContent);

    // Wrap in basic email-friendly HTML with styling
    const formattedOrgName = formatOrgDisplayName(tldrData.orgName);
    const styledHtml = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
        <h1 style="color: #1e293b; border-bottom: 2px solid #10b981; padding-bottom: 8px;">
          Weekly TLDR: ${formattedOrgName}
        </h1>
        <p style="color: #64748b; font-size: 14px;">
          ${tldrData.periodStart} to ${tldrData.periodEnd}
        </p>
        ${htmlContent}
      </div>
    `;

    // Copy HTML to clipboard for pasting into email client
    const success = await copyHtmlToClipboard(styledHtml);
    if (success) {
      setSnackbar({
        open: true,
        message: 'Copied as formatted email - paste into your email client!',
      });
    } else {
      setSnackbar({ open: true, message: 'Failed to copy.' });
    }
  }, [tldrData]);

  const handleEdit = useCallback(async () => {
    if (!tldrData || !editInstructions.trim()) return;

    setIsEditing(true);
    setError(null);

    try {
      const data = await editWeeklyTLDR(tldrData.sessionId, editInstructions);
      setTldrData({
        ...tldrData,
        markdownContent: data.markdownContent,
        sessionId: data.sessionId,
      });
      setEditInstructions('');
      setSnackbar({ open: true, message: 'Report updated!' });
    } catch (err) {
      console.error('Failed to edit TLDR:', err);
      setError('Failed to edit report. Please try again.');
    } finally {
      setIsEditing(false);
    }
  }, [tldrData, editInstructions]);

  const handleClose = useCallback(() => {
    setSelectedOrg(preselectedOrg);
    setTldrData(null);
    setError(null);
    setEditInstructions('');
    setShowActiveUsers(false);
    setShowInactiveUsers(false);
    onClose();
  }, [onClose, preselectedOrg]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '95vh',
            bgcolor: '#0f172a',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummarizeIcon sx={{ color: '#10b981' }} />
          Weekly TLDR Report
          <Chip
            label="AI Powered"
            size="small"
            sx={{
              ml: 1,
              bgcolor: 'rgba(16, 185, 129, 0.2)',
              color: '#10b981',
              fontSize: '0.7rem',
            }}
          />
          <Chip
            label="Internal"
            size="small"
            sx={{
              bgcolor: 'rgba(100, 116, 139, 0.2)',
              color: '#94a3b8',
              fontSize: '0.7rem',
            }}
          />
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate an internal weekly summary report with user activity breakdown, usage trends,
              and actionable insights for the customer success team.
            </Typography>

            <Autocomplete
              size="small"
              options={availableOrgs}
              getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
              value={selectedOrg}
              onChange={(_, newValue) => {
                setSelectedOrg(newValue);
                setTldrData(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  placeholder="Choose an organization..."
                />
              )}
              sx={{ minWidth: 300, mb: 2 }}
            />

            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!selectedOrg || isGenerating}
              startIcon={
                isGenerating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SummarizeIcon />
                )
              }
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Weekly TLDR'}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isGenerating && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={48} sx={{ color: '#10b981' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Analyzing weekly usage data and generating insights...
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1 }}>
                This may take 15-30 seconds
              </Typography>
            </Box>
          )}

          {tldrData && !isGenerating && (
            <Box>
              {/* Metrics Summary */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Week of {tldrData.periodStart} to {tldrData.periodEnd}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    size="small"
                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                    label={`${tldrData.activeUsers.filter((u) => u.accountType !== 'lite').length} active pro`}
                    sx={{ bgcolor: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}
                  />
                  {tldrData.activeUsers.filter((u) => u.accountType === 'lite').length > 0 && (
                    <Chip
                      size="small"
                      label={`${tldrData.activeUsers.filter((u) => u.accountType === 'lite').length} lite logged in`}
                      sx={{ bgcolor: 'rgba(148, 163, 184, 0.2)', color: '#94a3b8' }}
                    />
                  )}
                  <Chip
                    size="small"
                    icon={<PersonOffIcon sx={{ fontSize: 16 }} />}
                    label={`${tldrData.inactiveUsers.length} inactive`}
                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}
                  />
                  <Chip
                    size="small"
                    label={`${tldrData.totalQueries} queries`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${tldrData.totalWorkflows} workflows`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${tldrData.timeSavedHours.toFixed(1)}h saved`}
                    variant="outlined"
                  />
                </Box>
                {/* Platform Activity Stats */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {tldrData.projectsCreated > 0 && (
                    <Chip
                      size="small"
                      label={`${tldrData.projectsCreated} projects created`}
                      variant="outlined"
                      sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3b82f6' }}
                    />
                  )}
                  {tldrData.documentsUploaded > 0 && (
                    <Chip
                      size="small"
                      label={`${tldrData.documentsUploaded} docs uploaded`}
                      variant="outlined"
                      sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3b82f6' }}
                    />
                  )}
                  {tldrData.checklistsRun > 0 && (
                    <Chip
                      size="small"
                      label={`${tldrData.checklistsRun} checklists`}
                      variant="outlined"
                      sx={{ bgcolor: 'rgba(168, 85, 247, 0.1)', borderColor: '#a855f7' }}
                    />
                  )}
                  {tldrData.projectsActive > 0 && (
                    <Chip
                      size="small"
                      label={`${tldrData.projectsActive} active projects`}
                      variant="outlined"
                      sx={{ opacity: 0.7 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Active Users Expandable */}
              {tldrData.activeUsers.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setShowActiveUsers(!showActiveUsers)}
                    endIcon={showActiveUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: '#10b981', textTransform: 'none' }}
                  >
                    Active Users ({tldrData.activeUsers.length})
                  </Button>
                  <Collapse in={showActiveUsers}>
                    <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(16, 185, 129, 0.05)' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {tldrData.activeUsers
                          .filter((u) => u.accountType !== 'lite')
                          .map((user) => (
                            <Chip
                              key={user.userId}
                              size="small"
                              label={`${user.userName}: ${user.queryCount}q, ${user.workflowCount}w`}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        {tldrData.activeUsers
                          .filter((u) => u.accountType === 'lite')
                          .map((user) => (
                            <Chip
                              key={user.userId}
                              size="small"
                              label={`${user.userName} (lite - logged in)`}
                              sx={{
                                fontSize: '0.7rem',
                                bgcolor: 'rgba(148, 163, 184, 0.2)',
                                color: '#94a3b8',
                              }}
                            />
                          ))}
                      </Box>
                    </Paper>
                  </Collapse>
                </Box>
              )}

              {/* Inactive Users Expandable */}
              {tldrData.inactiveUsers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    size="small"
                    onClick={() => setShowInactiveUsers(!showInactiveUsers)}
                    endIcon={showInactiveUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: '#ef4444', textTransform: 'none' }}
                  >
                    Inactive Users ({tldrData.inactiveUsers.length})
                  </Button>
                  <Collapse in={showInactiveUsers}>
                    <Paper sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(239, 68, 68, 0.05)' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {tldrData.inactiveUsers.map((name, idx) => (
                          <Chip
                            key={idx}
                            size="small"
                            label={name}
                            sx={{ fontSize: '0.7rem', bgcolor: 'rgba(239, 68, 68, 0.1)' }}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Collapse>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* TLDR Content */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    AI-Generated Report
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                      onClick={handleCopy}
                      color={copied ? 'success' : 'inherit'}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleEmail}
                    >
                      Email
                    </Button>
                  </Box>
                </Box>

                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#1e293b',
                    maxHeight: 'calc(95vh - 550px)',
                    overflow: 'auto',
                    borderRadius: 2,
                    '& h1, & h2, & h3': {
                      color: '#f1f5f9',
                      borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
                      pb: 1,
                      mb: 2,
                    },
                    '& h2': {
                      fontSize: '1.25rem',
                      mt: 3,
                    },
                    '& p': {
                      color: '#cbd5e1',
                      lineHeight: 1.7,
                      mb: 1.5,
                    },
                    '& ul, & ol': {
                      color: '#cbd5e1',
                      pl: 3,
                    },
                    '& li': {
                      mb: 0.5,
                    },
                    '& strong': {
                      color: '#f1f5f9',
                    },
                    '& code': {
                      bgcolor: 'rgba(148, 163, 184, 0.1)',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.875em',
                    },
                  }}
                >
                  <ReactMarkdown>{tldrData.markdownContent}</ReactMarkdown>
                </Paper>
              </Box>

              {/* Edit Section */}
              <Divider sx={{ my: 2 }} />
              <Box ref={editSectionRef}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Refine the report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g., 'add more detail on user X', 'focus on concerns', 'shorten the action items'"
                    value={editInstructions}
                    onChange={(e) => setEditInstructions(e.target.value)}
                    disabled={isEditing}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleEdit();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleEdit}
                    disabled={!editInstructions.trim() || isEditing}
                    startIcon={isEditing ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                    sx={{ minWidth: 100 }}
                  >
                    {isEditing ? 'Editing...' : 'Edit'}
                  </Button>
                </Box>

                {/* Quick edit chips */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {EDIT_SUGGESTIONS.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      size="small"
                      onClick={() => setEditInstructions(suggestion)}
                      disabled={isEditing}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {!selectedOrg && !isGenerating && !tldrData && (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <SummarizeIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
              <Typography>Select an organization to generate a weekly TLDR report</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
